import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosObj } from "../../../config/apiClients";
import { ReferralClientsState } from "../../types";
import { queryParams } from "../../../utils/interface";
import { ReferralCashbackExportReferrer, ReferralClient } from "../../../utils/types";

const initialState: ReferralClientsState = {
  isLoading: false,
  referralClients: [],
  pagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
  },
  cashback: {
    isLoading: false,
    error: null,
    rows: [],
    totalItems: 0,
    draftCount: 0,
    draftAmount: 0,
    postedAmount: 0,
  },
};

export const getReferralCashbacks = createAsyncThunk(
  "/referral/cashback/getAll",
  async (
    params: { month: string; paramPage: number; paramLimit: number; search?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosObj.get("/referral/cashback", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Fetch Referral Cashback" });
    }
  },
);

export const generateReferralCashback = createAsyncThunk(
  "/referral/cashback/generate",
  async (month: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post("/referral/cashback/generate", { month });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Generate Referral Cashback" });
    }
  },
);

export const pushReferralCashback = createAsyncThunk(
  "/referral/cashback/push",
  async (
    payload: { month: string; excludedReferrerIds: string[] },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosObj.post("/referral/cashback/push", payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Push Referral Cashback" });
    }
  },
);

export const getReferralClients = createAsyncThunk(
  "/referralClients/getAll",
  async ({ paramPage, paramLimit, search }: queryParams, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(
        `/referral/clients?paramPage=${paramPage}&paramLimit=${paramLimit}${search ? `&search=${search}` : ""}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Fetch Referral Clients" });
    }
  },
);

const EXPORT_ALL_LIMIT = 100000;

export const getReferralClientsForExport = createAsyncThunk(
  "/referralClients/getForExport",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get("/referral/clients", {
        params: { paramPage: 1, paramLimit: EXPORT_ALL_LIMIT },
      });
      return (response.data?.payload?.referralClients || []) as ReferralClient[];
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Export Referral Clients" });
    }
  },
);

export const getReferralCashbackExport = createAsyncThunk(
  "/referral/cashback/export",
  async (month: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get("/referral/cashback/export", {
        params: { month },
      });
      return (response.data?.payload?.referrers || []) as ReferralCashbackExportReferrer[];
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to Export Referral Cashback" });
    }
  },
);

const referralClientsSlice = createSlice({
  name: "ReferralClients",
  initialState,
  reducers: {
    resetReferralClients: (state) => {
      state.referralClients = [];
      state.pagination = {
        totalItems: 0,
        currentPage: 1,
        totalPages: 1,
        hasNextPage: false,
      };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getReferralClients.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getReferralClients.fulfilled, (state, action) => {
        state.isLoading = false;
        state.referralClients = action.payload.payload.referralClients || [];
        state.pagination = {
          totalItems: action.payload.payload.totalItems || 0,
          currentPage: action.payload.payload.page || 1,
          totalPages: action.payload.payload.totalPages || 1,
          hasNextPage: action.payload.payload.hasNextPage || false,
        };
      })
      .addCase(getReferralClients.rejected, (state) => {
        state.isLoading = false;
        state.referralClients = [];
      })
      .addCase(getReferralCashbacks.pending, (state) => {
        state.cashback.isLoading = true;
        state.cashback.error = null;
      })
      .addCase(getReferralCashbacks.fulfilled, (state, action) => {
        const payload = action.payload?.payload;
        state.cashback = {
          isLoading: false,
          error: null,
          rows: payload?.referralCashbacks || [],
          totalItems: payload?.totalItems || 0,
          draftCount: payload?.draftCount || 0,
          draftAmount: payload?.draftAmount || 0,
          postedAmount: payload?.postedAmount || 0,
        };
      })
      .addCase(getReferralCashbacks.rejected, (state, action) => {
        state.cashback = {
          ...initialState.cashback,
          error:
            (action.payload as { message?: string })?.message ||
            "Failed to fetch referral cashback",
        };
      });
  },
});

export const { resetReferralClients } = referralClientsSlice.actions;

export default referralClientsSlice.reducer;
